import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import useScrollTriggeredCountUp from "../useScrollTriggeredCountUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserGroup,
  faChalkboardUser,
  faTrophy,
  faCalendarDays,
} from "@fortawesome/free-solid-svg-icons";

const Achievements = () => {
  const statRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.2 });

  const studentCount = useScrollTriggeredCountUp(statRefs[0], 1500, 1500);
  const teacherCount = useScrollTriggeredCountUp(statRefs[1], 60, 2000);
  const awardCount = useScrollTriggeredCountUp(statRefs[2], 100, 2000);
  const courseCount = useScrollTriggeredCountUp(statRefs[3], 1989, 2000);

  const stats = [
    {
      label: "Students",
      count: studentCount,
      ref: statRefs[0],
      icon: <FontAwesomeIcon icon={faUserGroup} className="text-white text-3xl" />,
      colorClass: "text-amber-300",
    },
    {
      label: "Teachers",
      count: teacherCount,
      ref: statRefs[1],
      icon: <FontAwesomeIcon icon={faChalkboardUser} className="text-white text-3xl" />,
      colorClass: "text-emerald-300",
    },
    {
      label: "Result",
      count: awardCount,
      ref: statRefs[2],
      icon: <FontAwesomeIcon icon={faTrophy} className="text-white text-3xl" />,
      colorClass: "text-sky-300",
    },
    {
      label: "Established Year",
      count: courseCount,
      ref: statRefs[3],
      icon: <FontAwesomeIcon icon={faCalendarDays} className="text-white text-3xl" />,
      colorClass: "text-rose-300",
    },
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
    }),
  };

  return (
    <section
      ref={sectionRef}
      className="px-6 md:px-12 lg:px-16 py-16 w-screen relative left-1/2 right-1/2 mx-[-50vw] mb-0 overflow-hidden"
    >
      {/* Background Video */}
      <video
        src="https://res.cloudinary.com/ddvpczzng/video/upload/v1765527168/ScrollCountUpCard_ibaheg.mp4"
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0"
      />

      {/* Light Dark Overlay */}
      <div className="absolute inset-0 bg-black/35 z-10"></div>

      {/* Content */}
      <div className="relative z-20 max-w-6xl mx-auto text-center">
        <div className="mb-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-white">
            Career Excellence
          </h2>
          <p className="text-lg mt-2 text-white">Shaping futures with skill and passion</p>
        </div>

        {/* Desktop = Row | Mobile = 2x2 grid */}
        <div
          className="
            grid 
            grid-cols-2 gap-y-10 gap-x-6 
            sm:flex sm:flex-row sm:justify-center sm:items-center sm:gap-12 lg:gap-16
          "
        >
          {stats.map((stat, index) => (
            <React.Fragment key={index}>
              <motion.div
                ref={stat.ref}
                variants={cardVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                custom={index}
                className="text-center"
              >
                <div className="flex justify-center mb-2">{stat.icon}</div>

                <div
                  className={`text-3xl sm:text-4xl md:text-5xl font-extrabold ${stat.colorClass}`}
                >
                  {stat.count}
                </div>

                <p className="mt-1 text-sm sm:text-base text-white">{stat.label}</p>
              </motion.div>

              {/* Divider — only visible on desktop */}
              {index !== stats.length - 1 && (
                <div className="hidden sm:flex items-center">
                  <div className="h-20 w-px bg-white/80 origin-center rotate-12" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Achievements;
